import { useEffect, useRef } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { StructureSetId } from '~/domain/changes/ids';
import {
    domainEventBus,
    RoiVisibilityChangeRequestedEvent,
} from '~/domain/events';
import { StructureSetReadModel } from '~/domain/readmodels/StructureSetReadModel';
import {
    comparablePair,
    compareModeActiveState,
    comparisonRoiAState,
    comparisonRoiBState,
} from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';

interface VisibilitySnapshot {
    structureSetId: StructureSetId;
    visibility: Map<number, boolean>;
}

function setRoiVisible(roiNumber: number, visible: boolean): void {
    void domainEventBus.publish(
        new RoiVisibilityChangeRequestedEvent(roiNumber, visible, 'user')
    );
}

/**
 * Replay a visibility snapshot onto `set`. Only ROIs present in both the
 * snapshot and the set are touched, so ROIs added or removed since the
 * snapshot keep their current visibility.
 */
function restoreVisibility(
    set: StructureSetReadModel,
    visibility: ReadonlyMap<number, boolean>
): void {
    set.roiArray.forEach((roi) => {
        const wanted = visibility.get(roi.roiNumber);
        if (wanted !== undefined && roi.visible !== wanted) {
            setRoiVisible(roi.roiNumber, wanted);
        }
    });
}

/**
 * While compare mode is active with a valid pair, hide ALL structures —
 * including the compared pair — so the comparison overlay is the only thing
 * drawn. The overlay renders the pair's contours itself (it reads ROI textures
 * via `getRoi()`, independent of visibility), so suppressing the normal contour
 * layer leaves the overlay as the sole, deliberate renderer of the comparison:
 * no unrelated contours competing, and no drawing the pair twice (once by the
 * contour layer at the global thickness, once by the overlay at its own).
 *
 * A compare session is scoped to the structure set it was opened on: ROI
 * numbers are only unique within one set, so carrying the pair across a switch
 * would silently relabel the comparison onto different structures. Switching
 * the current structure set therefore force-closes compare mode.
 *
 * The pre-compare visibility is captured when compare opens and restored when
 * it closes or on unmount. Visibility events can only address the CURRENT set,
 * so when the session ends because of a set switch the snapshot cannot be
 * replayed immediately; it is parked and restored when its set next becomes
 * current again.
 *
 * Mount once in an always-rendered viewer component.
 */
export function useComparisonRoiIsolation(): void {
    const compareModeActive = useRecoilValue(compareModeActiveState);
    const setCompareModeActive = useSetRecoilState(compareModeActiveState);
    const roiA = useRecoilValue(comparisonRoiAState);
    const roiB = useRecoilValue(comparisonRoiBState);
    const structureSet = useRecoilValue(currentStructureSetState);

    // The read model is re-created on every visibility change, so it is read via
    // a ref and kept out of the effect deps. The effects instead key on the
    // stable `structureSet.id` (a branded string) which changes only on a real
    // structure-set switch — never on a visibility edit — so they re-evaluate on
    // a switch without thrashing on each write the isolation itself makes.
    const structureSetRef = useRef(structureSet);
    structureSetRef.current = structureSet;
    const structureSetId = structureSet?.id;

    const savedRef = useRef<VisibilitySnapshot | null>(null);
    // Snapshots whose set was no longer current when their session ended (a
    // set switch closes compare, see the scoping effect). Replayed by the
    // healing effect when the set becomes current again.
    const pendingRestoresRef = useRef(
        new Map<StructureSetId, Map<number, boolean>>()
    );

    // Session scoping: close compare when the current set is no longer the one
    // the session captured its snapshot from (switch, or the set going away).
    // Declared before the snapshot effect so that on the opening commit it
    // still sees the previous session's cleared snapshot and does nothing.
    useEffect(() => {
        if (!compareModeActive) {
            return;
        }
        const saved = savedRef.current;
        if (saved && structureSetId !== saved.structureSetId) {
            setCompareModeActive(false);
        }
    }, [compareModeActive, structureSetId, setCompareModeActive]);

    // Snapshot visibility once per session — on open, or when a set first
    // becomes available if compare was opened without one. The cleanup restores
    // the snapshot onto its own set when that set is still current; otherwise
    // (session ended by a set switch) it parks the snapshot for the healing
    // effect below. The re-run after a mid-session switch is a no-op: the
    // parked snapshot is deliberately kept in `savedRef` so the session does
    // not re-capture the new set it is about to close on.
    useEffect(() => {
        if (!compareModeActive) {
            savedRef.current = null;
            return;
        }
        if (savedRef.current !== null) {
            return;
        }
        const openSet = structureSetRef.current;
        if (!openSet) {
            return;
        }
        const visibility = new Map<number, boolean>();
        openSet.roiArray.forEach((roi) => {
            visibility.set(roi.roiNumber, roi.visible);
        });
        savedRef.current = { structureSetId: openSet.id, visibility };
        // The map itself is stable for the hook's lifetime; captured so the
        // cleanup does not read `.current` (react-hooks/exhaustive-deps).
        const pendingRestores = pendingRestoresRef.current;

        return () => {
            const saved = savedRef.current;
            if (!saved) {
                return;
            }
            const currentSet = structureSetRef.current;
            if (currentSet && currentSet.id === saved.structureSetId) {
                savedRef.current = null;
                restoreVisibility(currentSet, saved.visibility);
            } else {
                // Visibility events only reach the current set; park the
                // snapshot until its set is visited again.
                pendingRestores.set(saved.structureSetId, saved.visibility);
            }
        };
    }, [compareModeActive, structureSetId]);

    // Hide every structure, including the compared pair: the overlay renders
    // the pair itself, so the normal contour layer is fully suppressed for a
    // clean, single-source comparison view. Re-runs on pair or structure-set
    // change. Gated on the session's own set being current — on the one
    // transient commit after a switch (before the scoping effect closes
    // compare) this keeps a stale pair from blanket-hiding the new set — and
    // on the pair still being present in it (ROIs can be deleted mid-session).
    useEffect(() => {
        if (!compareModeActive) {
            return;
        }
        const pair = comparablePair(roiA, roiB);
        const currentSet = structureSetRef.current;
        const saved = savedRef.current;
        if (
            !pair ||
            !currentSet ||
            !saved ||
            saved.structureSetId !== currentSet.id
        ) {
            return;
        }
        const [referenceRoi, testRoi] = pair;
        const rois = currentSet.roiArray;
        const pairPresent =
            rois.some((roi) => roi.roiNumber === referenceRoi) &&
            rois.some((roi) => roi.roiNumber === testRoi);
        if (!pairPresent) {
            return;
        }
        rois.forEach((roi) => {
            if (roi.visible) {
                setRoiVisible(roi.roiNumber, false);
            }
        });
    }, [compareModeActive, roiA, roiB, structureSetId]);

    // Healing: when the current set changes, replay the parked snapshot a past
    // session left for it (its restore could not run at close time because the
    // set was no longer current).
    useEffect(() => {
        const currentSet = structureSetRef.current;
        if (!currentSet) {
            return;
        }
        const pending = pendingRestoresRef.current.get(currentSet.id);
        if (!pending) {
            return;
        }
        pendingRestoresRef.current.delete(currentSet.id);
        restoreVisibility(currentSet, pending);
    }, [structureSetId]);
}
